import { Link, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';

import { ReachProOutlineLogoPDF } from './ReachProOutlineLogoPDF';
import { styles } from './RetailInvoicePDF.css';
import { RetailInvoicePDFProps } from './RetailInvoicePDF.types';
import {
  formatAmount,
  formatAmountShort,
  formatDate,
} from './RetailInvoicePDF.utils';

// ────────────────────────────────────────────────────────────
// Event date formatter: "M/D, h:mmA" e.g. "7/25, 7:30PM"
// ────────────────────────────────────────────────────────────
const formatEventDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'M/d, h:mmaaa')
      .replace('am', 'AM')
      .replace('pm', 'PM');
  } catch {
    return dateStr;
  }
};

// ────────────────────────────────────────────────────────────
// InvoiceHeaderSection
// ────────────────────────────────────────────────────────────

type InvoiceHeaderSectionProps = Pick<
  RetailInvoicePDFProps,
  'status' | 'totalAmount' | 'currency' | 'balanceDue' | 'labels'
>;

export const InvoiceHeaderSection = ({
  totalAmount,
  currency,
  balanceDue,
  labels,
}: InvoiceHeaderSectionProps) => {
  // POS-8630: badge is derived from balanceDue (not `status`) so all callers
  // get the correct Balance Due / Paid badge without changing their `status`.
  const isBalanceDue = balanceDue > 0;
  const badgeStyle = isBalanceDue
    ? styles.statusBadgeOutstanding
    : styles.statusBadgePaid;
  const badgeLabel = isBalanceDue
    ? (labels?.balanceDue ?? 'Balance Due')
    : (labels?.paid ?? 'Paid');

  return (
    <View style={styles.headerSection}>
      {/* Logo placeholder */}
      <Text style={styles.invoiceTitle}>
        {labels?.invoiceTitle ?? 'INVOICE'}
      </Text>

      {/* Right: INVOICE title + status + total */}
      <View style={styles.invoiceTitleGroup}>
        <View style={badgeStyle}>
          <Text>{badgeLabel}</Text>
        </View>
        <Text style={styles.totalAmountValue}>
          {formatAmount(totalAmount, currency)}
        </Text>
        <Text style={styles.totalAmountLabel}>
          {labels?.totalInvoiceValue ?? 'Total Invoice Value'}
        </Text>
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoiceMetadataRow
// ────────────────────────────────────────────────────────────

type InvoiceMetadataRowProps = Pick<
  RetailInvoicePDFProps,
  'invoiceNumber' | 'invoiceDate' | 'paymentTermsDays' | 'currency' | 'labels'
>;

export const InvoiceMetadataRow = ({
  invoiceNumber,
  invoiceDate,
  paymentTermsDays,
  currency,
  labels,
}: InvoiceMetadataRowProps) => (
  <View style={styles.metadataRow}>
    <View style={styles.metadataColumn}>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.invoiceNumber ?? 'Invoice #'}
        </Text>
        <Text style={styles.metadataValue}>{invoiceNumber}</Text>
      </View>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.invoiceDate ?? 'Invoice Date'}
        </Text>
        <Text style={styles.metadataValue}>{formatDate(invoiceDate)}</Text>
      </View>
    </View>
    <View style={styles.metadataColumn}>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.paymentTerms ?? 'Payment Terms'}
        </Text>
        <Text style={styles.metadataValue}>
          {paymentTermsDays != null
            ? `${paymentTermsDays} days`
            : (labels?.notAvailable ?? 'N/A')}
        </Text>
      </View>
      <View style={styles.metadataItem}>
        <Text style={styles.metadataLabel}>
          {labels?.currency ?? 'Currency'}
        </Text>
        <Text style={styles.metadataValue}>{currency}</Text>
      </View>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceAddressSection
// ────────────────────────────────────────────────────────────

type InvoiceAddressSectionProps = Pick<
  RetailInvoicePDFProps,
  | 'brokerName'
  | 'brokerCompany'
  | 'brokerAddress'
  | 'brokerEmail'
  | 'buyerName'
  | 'buyerCompany'
  | 'buyerEmail'
  | 'buyerAddress'
  | 'labels'
>;

export const InvoiceAddressSection = ({
  brokerName,
  brokerCompany,
  brokerAddress,
  brokerEmail,
  buyerName,
  buyerCompany,
  buyerEmail,
  buyerAddress,
  labels,
}: InvoiceAddressSectionProps) => (
  <View style={styles.addressSection}>
    {/* FROM */}
    <View style={styles.addressColumn}>
      <Text style={styles.addressSectionTitle}>{labels?.from ?? 'FROM'}</Text>
      <Text style={styles.addressName}>{brokerName}</Text>
      {brokerCompany ? (
        <Text style={styles.addressDetail}>{brokerCompany}</Text>
      ) : null}
      {brokerAddress ? (
        <Text style={styles.addressDetail}>{brokerAddress}</Text>
      ) : null}
      {brokerEmail ? (
        <Text style={styles.addressDetail}>{brokerEmail}</Text>
      ) : null}
    </View>

    {/* BILLED TO */}
    <View style={styles.addressColumnRight}>
      <Text style={styles.addressSectionTitle}>
        {labels?.billedTo ?? 'BILLED TO'}
      </Text>
      {buyerName ? (
        <Text style={styles.addressName}>{buyerName}</Text>
      ) : (
        <Text style={styles.addressName}>—</Text>
      )}
      {buyerCompany ? (
        <Text style={styles.addressDetail}>{buyerCompany}</Text>
      ) : null}
      {buyerAddress ? (
        <Text style={styles.addressDetail}>{buyerAddress}</Text>
      ) : null}
      {buyerEmail ? (
        <Text style={styles.addressDetail}>{buyerEmail}</Text>
      ) : null}
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceOrderDetailTable
// ────────────────────────────────────────────────────────────

type InvoiceOrderDetailTableProps = Pick<
  RetailInvoicePDFProps,
  | 'lineItems'
  | 'totalAmount'
  | 'balanceDue'
  | 'balanceDueDate'
  | 'currency'
  | 'status'
  | 'paymentsMade'
  | 'orderDetailRows'
  | 'labels'
>;

export const InvoiceOrderDetailTable = ({
  lineItems,
  balanceDue,
  balanceDueDate,
  currency,
  status,
  paymentsMade,
  orderDetailRows,
  labels,
}: InvoiceOrderDetailTableProps) => {
  // POS-8630: payment-reference items (they carry paymentReferenceId/referenceLabel)
  // are rendered ONLY in the payment section below — exclude them from the Overview
  // table and the Subtotal so the Subtotal equals the amount owed.
  const overviewLineItems = lineItems.filter(
    (item) => item.paymentReferenceId == null && item.referenceLabel == null
  );
  const subtotal = overviewLineItems.reduce(
    (sum, item) => sum + item.amount,
    0
  );
  const computedPaymentsMade = paymentsMade ?? subtotal - balanceDue;
  // POS-8630: Total Proceed = sum of per-event proceeds
  const totalProceed = orderDetailRows.reduce(
    (sum, row) => sum + (row.proceed ?? 0),
    0
  );
  // POS-8630: payment lines to render as purple external links (only entries
  // that actually represent a payment — never plain proceeds/charge rows).
  const paymentLines = lineItems.filter(
    (item) => item.paymentReferenceId != null || item.referenceLabel != null
  );

  const isPaid = status === 'Paid';
  const rowStyle = isPaid ? styles.balanceDueRowPaid : styles.balanceDueRow;
  const labelStyle = isPaid
    ? styles.balanceDueLabelPaid
    : styles.balanceDueLabel;
  const dateStyle = isPaid ? styles.balanceDueDatePaid : styles.balanceDueDate;
  const amountStyle = isPaid
    ? styles.balanceDueAmountPaid
    : styles.balanceDueAmount;

  return (
    <View style={styles.orderDetailSection}>
      <Text style={styles.orderDetailTitle}>
        {labels?.orderDetail ?? 'ORDER DETAIL'}
      </Text>

      {/* Event details table — POS-8630: one row per event */}
      <View style={[styles.tableContainer, { marginBottom: 8 }]}>
        {/* Header */}
        <View style={styles.tableHeaderRow}>
          <View style={[styles.tableHeaderCellWrapper, { flex: 3 }]}>
            <Text style={styles.tableHeaderCell}>
              {labels?.event ?? 'Event'}
            </Text>
          </View>
          <View style={[styles.tableHeaderCellWrapper, { flex: 1.5 }]}>
            <Text style={styles.tableHeaderCell}>
              {labels?.section ?? 'Section'}
            </Text>
          </View>
          <View style={[styles.tableHeaderCellWrapper, { flex: 0.8 }]}>
            <Text style={styles.tableHeaderCell}>{labels?.row ?? 'Row'}</Text>
          </View>
          <View style={[styles.tableHeaderCellWrapper, { flex: 1.2 }]}>
            <Text style={styles.tableHeaderCell}>
              {labels?.seatRange ?? 'Seat Range'}
            </Text>
          </View>
          <View style={[styles.tableHeaderCellWrapper, { flex: 0.6 }]}>
            <Text style={styles.tableHeaderCell}>{labels?.qty ?? 'Qty'}</Text>
          </View>
          <View
            style={[
              styles.tableHeaderCellWrapper,
              { flex: 1.5, borderRightWidth: 0 },
            ]}
          >
            <Text style={styles.tableHeaderCell}>
              {labels?.proceedHeader ?? 'Proceed ($)'}
            </Text>
          </View>
        </View>
        {/* Data rows — one per event; no row-level padding; each cell uses
            paddingLeft like the header */}
        {orderDetailRows.map((row, index) => (
          <View key={index} style={[styles.tableRow, { padding: 0 }]}>
            {/* Event cell: name + date @ venue */}
            <View style={[{ flex: 3, overflow: 'hidden', paddingLeft: 10 }]}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-expect-error numberOfLines is a valid runtime prop but missing from @react-pdf/renderer types */}
              <Text style={styles.eventNameText} numberOfLines={1}>
                {row.eventName ?? '—'}
              </Text>
              {(row.eventDate || row.venueName) && (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error numberOfLines is a valid runtime prop but missing from @react-pdf/renderer types
                <Text style={styles.eventSubText} numberOfLines={1}>
                  {[
                    row.eventDate ? formatEventDate(row.eventDate) : '',
                    row.venueName ? `@ ${row.venueName}` : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </Text>
              )}
            </View>
            <View style={{ flex: 1.5, paddingLeft: 10 }}>
              <Text style={{ fontSize: 9, color: '#111827' }}>
                {row.section ?? '—'}
              </Text>
            </View>
            <View style={{ flex: 0.8, paddingLeft: 10 }}>
              <Text style={{ fontSize: 9, color: '#111827' }}>
                {row.row ?? '—'}
              </Text>
            </View>
            <Text
              style={[styles.eventCellRight, { flex: 1.2, paddingRight: 6 }]}
            >
              {row.seats ??
                (row.seatFrom && row.seatTo
                  ? `${row.seatFrom}–${row.seatTo}`
                  : (row.seatFrom ?? '—'))}
            </Text>
            <Text
              style={[styles.eventCellRight, { flex: 0.6, paddingRight: 6 }]}
            >
              {row.qty ?? '—'}
            </Text>
            <Text
              style={[styles.eventCellRight, { flex: 1.5, paddingRight: 10 }]}
            >
              {formatAmountShort(row.proceed ?? 0)}
            </Text>
          </View>
        ))}
      </View>

      {/* Table 1: line items only */}
      <View style={styles.tableContainer}>
        {/* Header */}
        <View style={styles.tableHeaderRow}>
          <View style={[styles.tableHeaderCellWrapper, { flex: 3 }]}>
            <Text style={styles.tableHeaderCell}>
              {labels?.overview ?? 'Overview'}
            </Text>
          </View>
          <View style={[styles.tableHeaderCellWrapper, { flex: 1 }]}>
            <Text style={styles.tableHeaderCellRight}>
              {labels?.ratePercent ?? 'Rate %'}
            </Text>
          </View>
          <View
            style={[
              styles.tableHeaderCellWrapper,
              { flex: 1.5, borderRightWidth: 0 },
            ]}
          >
            <Text style={styles.tableHeaderCellRight}>
              {labels?.amountHeader ?? 'Amount $'}
            </Text>
          </View>
        </View>

        {/* Flat line items — POS-8630: payment-reference items excluded (shown only
            in the payment section below). */}
        {overviewLineItems.map((item, index) => {
          const rowStyle = index % 2 === 1 ? styles.tableRowAlt : {};

          return (
            <View key={index} style={[styles.tableRow, rowStyle]}>
              <View style={[styles.tableCell, { flex: 3 }]}>
                <Text>
                  {item.description || '—'}
                  {item.costType ? (
                    <Text style={{ fontSize: 8, color: '#6B7280' }}>
                      {'  '}
                      {item.costType}
                    </Text>
                  ) : null}
                </Text>
                {item.paymentReferenceId ? (
                  <Text style={{ fontSize: 8, opacity: 0.6 }}>
                    {labels?.refPrefix ?? 'Ref: '}
                    {item.paymentReferenceId}
                  </Text>
                ) : null}
              </View>
              <Text style={styles.tableCellRate}>
                {item.ratePercent != null ? `${item.ratePercent} %` : '—'}
              </Text>
              <Text style={styles.tableCellAmount}>
                {formatAmountShort(item.amount)}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Table 2: totals */}
      <View style={[styles.tableContainer, { marginTop: 8 }]}>
        {/* Subtotal row */}
        <View style={styles.subtotalRow}>
          <Text style={styles.subtotalLabel}>
            {labels?.subtotal ?? 'Subtotal'}
          </Text>
          <View style={{ flex: 1 }} />
          <Text style={styles.subtotalAmount}>
            {formatAmountShort(subtotal)}
          </Text>
        </View>
        {/* Total Proceed row — POS-8630: sum of per-event proceeds */}
        <View
          style={[
            styles.subtotalRow,
            {
              borderTopWidth: 1,
              borderTopColor: '#E5E7EB',
              borderTopStyle: 'solid',
            },
          ]}
        >
          <Text style={styles.subtotalLabel}>
            {labels?.totalProceed ?? 'Total Proceed'}
          </Text>
          <View style={{ flex: 1 }} />
          <Text style={styles.subtotalAmount}>
            {formatAmountShort(totalProceed)}
          </Text>
        </View>
        {/* Payment row */}
        {computedPaymentsMade > 0 ? (
          <View
            style={[
              styles.subtotalRow,
              {
                borderTopWidth: 1,
                borderTopColor: '#E5E7EB',
                borderTopStyle: 'solid',
              },
            ]}
          >
            <Text style={styles.subtotalLabel}>
              {labels?.payment ?? 'Payment'}
            </Text>
            <View style={{ flex: 1 }} />
            <Text style={styles.subtotalAmount}>
              {formatAmountShort(computedPaymentsMade)}
            </Text>
          </View>
        ) : null}
        {/* Per-payment-line rows — POS-8630: purple external links.
            Only rendered when at least one lineItem qualifies as a payment. */}
        {paymentLines.map((item, index) => {
          const label =
            item.referenceLabel ??
            (item.paymentReferenceId
              ? `Retail - ${item.paymentReferenceId}`
              : item.description);
          return (
            <View key={`pay-${index}`} style={styles.paymentLineRow}>
              <View style={{ flex: 3 }}>
                {item.referenceUrl ? (
                  <Link src={item.referenceUrl} style={styles.paymentLink}>
                    {/* POS-8630: no PDF-safe external-link icon asset exists in
                        the RetailInvoice dir; using a text glyph instead. */}
                    {label} ↗
                  </Link>
                ) : (
                  <Text style={styles.paymentLink}>
                    {/* POS-8630: no PDF-safe external-link icon asset exists in
                        the RetailInvoice dir; using a text glyph instead. */}
                    {label} ↗
                  </Text>
                )}
              </View>
              <Text style={styles.paymentMethodLabel}>
                {item.paymentMethodLabel ?? '—'}
              </Text>
              <Text style={[styles.paymentLineAmount, { flex: 1.5 }]}>
                {formatAmountShort(item.amount)}
              </Text>
            </View>
          );
        })}
        {/* Balance Due row */}
        <View
          style={[
            rowStyle,
            {
              borderTopWidth: 1,
              borderTopColor: '#E5E7EB',
              borderTopStyle: 'solid',
            },
          ]}
        >
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <Text style={labelStyle}>
              {labels?.balanceDue ?? 'Balance Due'}
            </Text>
            {balanceDueDate ? (
              <Text style={dateStyle}> • {formatDate(balanceDueDate)}</Text>
            ) : null}
          </View>
          <Text style={amountStyle}>{formatAmount(balanceDue, currency)}</Text>
        </View>
      </View>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoicePaymentInfoSection
// ────────────────────────────────────────────────────────────

type InvoicePaymentInfoSectionProps = Pick<
  RetailInvoicePDFProps,
  'paymentMethod' | 'paymentReference' | 'labels'
>;

export const InvoicePaymentInfoSection = ({
  paymentMethod,
  paymentReference,
  labels,
}: InvoicePaymentInfoSectionProps) => (
  <View style={styles.paymentInfoSection}>
    <Text style={styles.paymentInfoTitle}>
      {labels?.paymentInfoAndTerms ?? 'PAYMENT INFO & TERMS'}
    </Text>

    {paymentMethod ? (
      <View style={styles.paymentInfoRow}>
        <Text style={styles.paymentInfoLabel}>
          {labels?.paymentMethod ?? 'Payment Method'}
        </Text>
        <Text style={styles.paymentInfoValue}>{paymentMethod}</Text>
      </View>
    ) : null}

    <View style={styles.paymentInfoRow}>
      <Text style={styles.paymentInfoLabel}>
        {labels?.instructions ?? 'Instructions'}
      </Text>
      <Text style={styles.paymentInfoValue}>
        {labels?.payingRemainingBalance ??
          'Paying remaining balance via agreed payment method.'}
      </Text>
    </View>

    {paymentReference ? (
      <View style={styles.paymentInfoRow}>
        <Text style={styles.paymentInfoLabel}>
          {labels?.paymentReference ?? 'Payment Reference'}
        </Text>
        <Text style={styles.paymentInfoValue}>{paymentReference}</Text>
      </View>
    ) : null}

    <View style={styles.paymentInfoRow}>
      <Text style={styles.paymentInfoLabel}>
        {labels?.refundPolicy ?? 'Refund Policy'}
      </Text>
      <Text style={styles.paymentInfoValue}>
        {labels?.allSalesFinal ?? 'All sales are final.'}
      </Text>
    </View>
  </View>
);

// ────────────────────────────────────────────────────────────
// InvoiceBrokerNotesSection
// ────────────────────────────────────────────────────────────

type InvoiceBrokerNotesSectionProps = Pick<
  RetailInvoicePDFProps,
  'brokerNotes' | 'labels'
>;

export const InvoiceBrokerNotesSection = ({
  brokerNotes,
  labels,
}: InvoiceBrokerNotesSectionProps) => {
  if (!brokerNotes) return null;

  return (
    <View style={styles.brokerNotesSection}>
      <Text style={styles.brokerNotesTitle}>
        {labels?.brokerNotes ?? 'BROKER NOTES'}
      </Text>
      <Text style={styles.brokerNotesText}>{brokerNotes}</Text>
    </View>
  );
};

// ────────────────────────────────────────────────────────────
// InvoiceFooter
// ────────────────────────────────────────────────────────────

export const InvoiceFooter = () => (
  <View style={styles.footer}>
    <ReachProOutlineLogoPDF width={24} height={24} color="#221e1f" />
  </View>
);
